import { useEffect, useState } from 'react';
import { pointService } from '../../services/api';
import { Aviso, BotonPrimario, BotonSecundario, Campo, Entrada, Selector, Tarjeta, Seccion } from './ui';

type Terminal = { id: string; operating_mode?: string; pos_id?: string };

export default function SeccionMercadoPago() {
  const [token, setToken] = useState('');
  const [tokenGuardado, setTokenGuardado] = useState(false);
  const [terminal, setTerminal] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [terminales, setTerminales] = useState<Terminal[]>([]);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    let activo = true;
    pointService.ajustes().then(({ data }) => {
      if (!activo) return;
      setTokenGuardado(data.tokenConfigured); setTerminal(data.terminalId); setEnabled(data.enabled);
    }).catch((e: any) => {
      if (activo) setError(e.response?.data?.error || 'No se pudo cargar la configuración de Mercado Pago.');
    }).finally(() => { if (activo) setCargando(false); });
    return () => { activo = false; };
  }, []);

  const buscar = async () => {
    setOcupado(true); setError(''); setMensaje('');
    try {
      const { data } = await pointService.probar(token.trim() || undefined);
      const lista: Terminal[] = data.terminals;
      setTerminales(lista);
      if (!lista.some(t => t.id === terminal)) {
        setTerminal(lista.length === 1 ? lista[0].id : '');
        setEnabled(false);
      }
      setMensaje(lista.length ? `Conexión correcta. Terminales encontradas: ${lista.length}.` : 'Conexión correcta. Vincula tu Point Smart 2 a una sucursal y caja en Mercado Pago para que aparezca aquí.');
    } catch (e: any) {
      setTerminales([]);
      setError(e.response?.data?.error || 'No se pudo conectar con Mercado Pago. Intenta de nuevo.');
    } finally { setOcupado(false); }
  };

  const guardar = async () => {
    setOcupado(true); setError(''); setMensaje('');
    try {
      const { data } = await pointService.guardarAjustes({ accessToken: token.trim() || undefined, terminalId: terminal, enabled });
      setToken(''); setTokenGuardado(data.tokenConfigured); setTerminal(data.terminalId); setEnabled(data.enabled);
      setMensaje(data.enabled ? 'Mercado Pago guardado. Ya puedes enviar cobros a tu terminal desde Cuentas.' : 'Configuración guardada. Los cobros con Point están desactivados.');
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo guardar la configuración.');
    } finally { setOcupado(false); }
  };

  if (cargando) return <div role="status">Cargando configuración de Mercado Pago…</div>;
  const elegida = terminales.find(t => t.id === terminal);
  const faltaPDV = !!elegida && elegida.operating_mode !== 'PDV';
  return <Seccion>
    <Tarjeta titulo="Conectar Mercado Pago" descripcion="Configura tu Point Smart 2 de México para recibir el importe directamente desde CHARGOY.">
      <Campo label="Access Token" hint={tokenGuardado ? 'Ya hay una credencial guardada. Deja el campo vacío para conservarla o pega una nueva para reemplazarla.' : 'Pega el Access Token de tu aplicación Point en Mercado Pago Developers.'}>
        <Entrada type="password" name="mercado-pago-access-token" autoComplete="new-password" aria-label="Access Token de Mercado Pago"
          value={token} disabled={ocupado} placeholder={tokenGuardado ? 'Credencial guardada' : 'Pega tu Access Token'}
          onChange={e => { setToken(e.target.value); setTerminales([]); setTerminal(''); setEnabled(false); setMensaje(''); }} />
      </Campo>
      <div><BotonSecundario disabled={ocupado || (!token.trim() && !tokenGuardado)} onClick={buscar}>
        {ocupado ? 'Procesando…' : 'Probar conexión y buscar terminales'}
      </BotonSecundario></div>
      <a href="https://www.mercadopago.com.mx/developers/panel/app" target="_blank" rel="noreferrer" style={{ color: 'var(--brand-texto)' }}>Abrir Mercado Pago Developers</a>
    </Tarjeta>
    <Tarjeta titulo="Terminal de cobro" descripcion="Selecciona la terminal vinculada a tu cuenta. Debe estar configurada en modo PDV.">
      <Campo label="Point Smart 2">
        <Selector aria-label="Terminal de Mercado Pago" value={terminal} disabled={ocupado} onChange={e => { setTerminal(e.target.value); setMensaje(''); }}>
          <option value="">Selecciona una terminal</option>
          {terminal && !terminales.some(t => t.id === terminal) && <option value={terminal}>{terminal} · guardada</option>}
          {terminales.map(t => <option key={t.id} value={t.id}>{t.id} · {t.operating_mode || 'Sin modo PDV'}</option>)}
        </Selector>
      </Campo>
      {faltaPDV && <Aviso tono="alerta">Activa el modo PDV de esta terminal en Mercado Pago y vuelve a buscar terminales.</Aviso>}
      <label style={{ display: 'flex', gap: 12, alignItems: 'center', fontWeight: 700 }}>
        <input type="checkbox" checked={enabled} disabled={ocupado || faltaPDV || !terminal || (!tokenGuardado && !token.trim())} onChange={e => setEnabled(e.target.checked)} />
        Activar cobros con Mercado Pago Point
      </label>
      <Aviso>Se cobra la cuenta completa con tarjeta y propina opcional. Antes de cambiar la conexión, completa o cancela cualquier cobro pendiente en la terminal y consulta su resultado en Cuentas.</Aviso>
      {error && <div role="alert"><Aviso tono="alerta">{error}</Aviso></div>}
      {mensaje && <div role="status"><Aviso tono="ok">{mensaje}</Aviso></div>}
      <div><BotonPrimario disabled={ocupado} onClick={guardar}>{ocupado ? 'Procesando…' : 'Guardar Mercado Pago'}</BotonPrimario></div>
      <small style={{ color: 'var(--muted)' }}>Esta sección se guarda con su propio botón. Los cambios se aplican al guardar.</small>
    </Tarjeta>
  </Seccion>;
}
